let _ = t => t,
  _t;
import { useState } from 'react';
import { NavMarker } from 'react-rhumb';
import { usePagination } from 'academy-common-lib/features/pagination/paginationHooks';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { H3 } from 'foundations-components/transitional/components/H3';
import { CollapsedButtonsGroup } from '../../buttons/CollapsedButtonsGroup';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import styledWithTheme from 'foundations-theming/utils';
import { LearningContentCard } from 'academy-common-lib/features/learningContentCard/components/LearningContentCard';
import { LearningContentCardsSkeleton } from 'academy-common-lib/components/Skeletons/LearningContentCardsSkeleton';
import NoResultsError from 'academy-common-lib/components/Errors/NoResultsError';
import ApiError from '../../../components/Errors/ApiError';
import { ACADEMY_ONBOARDING_URL_FRAGMENT } from '../../../constants/Navigation.legacy';
import { Button } from 'foundations-components/transitional/components/Button';
import { useOnboardingResourcesWithEnglishFallback } from 'academy-common-lib/features/academyOnboarding/resourcesQueries';
import UsageTracker from '../../../utils/UsageTracker';
import OnboardingSectionLayout from './OnboardingSectionLayout';
import { ACADEMY_ONBOARDING_SECTION_IDS } from '../constants/sectionAnchors';
import { AO_SUPPORTED_LANGUAGES } from '../constants/supportedLanguages';
import { FiltersBar } from 'academy-common-lib/features/filters/components/FiltersCard';
import { LanguageFilter } from 'academy-common-lib/features/filters/components/LanguageFilter';
import { ProductFilter } from './ProductFilter';
import getLang from 'academy-common-lib/utils/getLang';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TAB_OPTIONS = [{
  message: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.resources.tabAll"
  }),
  value: 'ALL'
}, {
  message: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.resources.tabPlaylists"
  }),
  value: 'PLAYLISTS'
}, {
  message: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.resources.tabRecordings"
  }),
  value: 'RECORDINGS'
}, {
  message: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.resources.tabDocuments"
  }),
  value: 'DOCUMENTS'
}];
const TAB_CONTENT_TYPES = {
  ALL: ['LEARNING_PATH', 'SHORT_FORM_CONTENT', 'TEMPLATE'],
  PLAYLISTS: ['LEARNING_PATH'],
  RECORDINGS: ['SHORT_FORM_CONTENT'],
  DOCUMENTS: ['TEMPLATE']
};
const LARGE_PAGE_SIZE = 12;
const SMALL_PAGE_SIZE = 8;
const ResourcesGrid = styledWithTheme.div(_t || (_t = _`
  display: grid;
  grid-template-columns: ${0};
  gap: ${0};
  width: 100%;
`), ({
  $isMobile
}) => $isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))', ({
  $isMobile,
  theme
}) => $isMobile ? theme.space['200'] : theme.space['600']);
const ResourcesSection = () => {
  var _TAB_OPTIONS$find;
  const {
    isMobile,
    isTablet,
    isDesktop
  } = useMediaQuery();
  const [activeTab, setActiveTab] = useState('ALL');
  const {
    currentPage,
    setPage
  } = usePagination();
  const [productGroups, setProductGroups] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const paramLanguage = selectedLanguages[0];
  const language = paramLanguage !== null && paramLanguage !== void 0 ? paramLanguage : getLang();
  const pageSize = isDesktop ? LARGE_PAGE_SIZE : SMALL_PAGE_SIZE;
  const contentTypes = TAB_CONTENT_TYPES[activeTab];
  const isDefaultUnfilteredView = activeTab === 'ALL' && productGroups.length === 0;
  const {
    resources,
    loading: isLoading,
    error,
    totalCount
  } = useOnboardingResourcesWithEnglishFallback({
    contentTypes,
    productGroups,
    language,
    pageLength: pageSize,
    offset: (currentPage - 1) * pageSize,
    enableFallback: !paramLanguage && isDefaultUnfilteredView
  });
  const totalPages = Math.ceil(totalCount / pageSize);
  const handleTabChange = tab => {
    if (tab !== activeTab) {
      UsageTracker.trackStandalone('Learning Center Interaction', {
        action: 'onboarding-resource-tab-select',
        appearsIn: 'academy-onboarding-resources',
        tab
      });
    }
    setActiveTab(tab);
    setPage(1);
  };
  const handleProductChange = products => {
    setProductGroups(products);
    setPage(1);
  };
  const handleLanguageChange = languages => {
    setSelectedLanguages(languages);
    setPage(1);
  };
  const handleResetFilters = () => {
    setActiveTab('ALL');
    setProductGroups([]);
    setSelectedLanguages([]);
    setPage(1);
  };
  const handlePageChange = evt => {
    setPage(evt.target.value);
  };
  const isEmpty = !isLoading && !error && (!resources || resources.length === 0);
  return /*#__PURE__*/_jsxs(OnboardingSectionLayout, {
    id: ACADEMY_ONBOARDING_SECTION_IDS.learningResources,
    paddingBlockStart: "600",
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: isMobile || isTablet ? 'stretch' : 'center',
      flexDirection: isMobile || isTablet ? 'column' : 'row',
      gap: "400",
      justifyContent: "space-between",
      marginBlockEnd: "600",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flex: "1",
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(H3, {
          marginBlockEnd: "200",
          typography: "heading-300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.academyOnboarding.resources.title"
          })
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.academyOnboarding.resources.description"
          })
        })]
      }), /*#__PURE__*/_jsxs(FiltersBar, {
        shouldRenderClearFilters: false,
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.academyOnboarding.resources.filterLabel"
          })
        }), /*#__PURE__*/_jsx(ProductFilter, {
          appearsIn: "academy-onboarding-resources",
          onChange: handleProductChange,
          value: productGroups
        }), /*#__PURE__*/_jsx(LanguageFilter, {
          appearsIn: "academy-onboarding-resources",
          languages: AO_SUPPORTED_LANGUAGES,
          onChange: handleLanguageChange,
          value: selectedLanguages
        })]
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "800",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(CardWrapper, {
          marginBlockEnd: "600",
          padding: "400",
          children: /*#__PURE__*/_jsx(CollapsedButtonsGroup, {
            alignItems: "center",
            dropdownProps: isMobile ? {
              buttonText: (_TAB_OPTIONS$find = TAB_OPTIONS.find(o => o.value === activeTab)) === null || _TAB_OPTIONS$find === void 0 ? void 0 : _TAB_OPTIONS$find.message
            } : undefined,
            children: TAB_OPTIONS.map(option => /*#__PURE__*/_jsx(Button, {
              active: activeTab === option.value,
              onClick: () => handleTabChange(option.value),
              use: "tertiary-extra-light",
              children: option.message
            }, option.value))
          })
        }), !isLoading && !error && /*#__PURE__*/_jsx(NavMarker, {
          name: "ACADEMY_ONBOARDING_RESOURCES_LOADED"
        }), isLoading ? /*#__PURE__*/_jsx(ResourcesGrid, {
          $isMobile: isMobile,
          children: /*#__PURE__*/_jsx(LearningContentCardsSkeleton, {
            maxSkeletonListLength: pageSize,
            skeletonCardSize: "small",
            use: "on-light"
          })
        }) : error ? /*#__PURE__*/_jsx(ApiError, {
          error: error,
          markerDefault: "ACADEMY_ONBOARDING_RESOURCES_API_ERROR",
          resetLink: ACADEMY_ONBOARDING_URL_FRAGMENT
        }) : isEmpty ? /*#__PURE__*/_jsx(NoResultsError, {
          onReset: handleResetFilters,
          resetLink: ACADEMY_ONBOARDING_URL_FRAGMENT
        }) : /*#__PURE__*/_jsx(ResourcesGrid, {
          $isMobile: isMobile,
          children: resources.map(resource => /*#__PURE__*/_jsx(LearningContentCard, {
            item: resource,
            overviewType: "academy-onboarding-resources",
            size: "small"
          }, resource.id))
        })]
      }), totalPages > 1 && /*#__PURE__*/_jsx(Paginator, {
        onPageChange: handlePageChange,
        page: currentPage,
        pageCount: totalPages,
        showFirstLastButtons: true
      })]
    })]
  });
};
export default ResourcesSection;